import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    LineElement,
    PointElement,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';
import annotationPlugin from 'chartjs-plugin-annotation';
import { Chart } from 'react-chartjs-2';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import Modal from 'react-modal';
import styles from './NormalDistributionChartWithDropdown.module.css';
import GoToDashboardButton from '../components/GoToDashboardButton';
import SourceFileSummary from './SourceFileSummary';
import { exportChartAsPNG } from './controlChart/controlChartExport';
import {
    fetchNormalDistribution,
    fetchNormalDistributionFilters,
} from './normalDistribution/normalDistributionApi';
import {
    buildNormalDistributionChartData,
    buildNormalDistributionOptions,
    getCapabilityMetrics,
    normalizeNormalDistribution,
} from './normalDistribution/normalDistributionData';

ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, Title, Tooltip, Legend, annotationPlugin);
Modal.setAppElement('#root');

const NormalDistributionChartWithDropdown = () => {
    const [stepNumbers, setStepNumbers] = useState([]);
    const [selectedStepNumber, setSelectedStepNumber] = useState('');
    const [ateSwVersions, setAteSwVersions] = useState([]);
    const [testspecs, setTestspecs] = useState([]);
    const [selectedAteSwVersion, setSelectedAteSwVersion] = useState('');
    const [selectedTestspec, setSelectedTestspec] = useState('');
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [selectedFamilies, setSelectedFamilies] = useState([]);
    const [normalDistData, setNormalDistData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [lsl] = useState('');
    const [usl] = useState('');
    const chartRef = useRef(null);

    const chartData = useMemo(() => buildNormalDistributionChartData(normalDistData), [normalDistData]);
    const chartOptions = useMemo(() => buildNormalDistributionOptions(normalDistData), [normalDistData]);
    const metrics = useMemo(() => getCapabilityMetrics(normalDistData), [normalDistData]);

    useEffect(() => {
        const loadFilters = async () => {
            try {
                const filters = await fetchNormalDistributionFilters({ selectedFamilies });
                setStepNumbers(filters.stepNumbers);
                setAteSwVersions(filters.ateSwVersions);
                setTestspecs(filters.testspecs);
            } catch (fetchError) {
                console.error('Error fetching normal distribution filters:', fetchError);
                setError('Failed to fetch filters.');
            }
        };

        loadFilters();
    }, [selectedFamilies]);

    const fetchNormalDistributionData = async () => {
        if (!selectedStepNumber) {
            setError('Please select a step number.');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const responseData = await fetchNormalDistribution({
                selectedStepNumber,
                startDate,
                endDate,
                selectedAteSwVersion,
                selectedTestspec,
                lsl,
                usl,
                selectedFamilies,
            });
            setNormalDistData(normalizeNormalDistribution(responseData));
        } catch (fetchError) {
            console.error('Error fetching normal distribution data:', fetchError);
            setNormalDistData(null);
            setError(fetchError.response?.data?.error || 'Failed to fetch data.');
        } finally {
            setLoading(false);
        }
    };

    const handleResetFilters = () => {
        setSelectedStepNumber('');
        setSelectedAteSwVersion('');
        setSelectedTestspec('');
        setStartDate(null);
        setEndDate(null);
        setSelectedFamilies([]);
        setNormalDistData(null);
        setError(null);
    };

    return (
        <div className={styles.container}>
            <GoToDashboardButton />

            <header className={styles.pageHeader}>
                <div>
                    <p className={styles.eyebrow}>Process capability</p>
                    <h2 className={styles.title}>Normal Distribution Chart</h2>
                    <p className={styles.description}>
                        Compare measured values against histogram counts, the fitted normal curve, and capability limits.
                    </p>
                </div>
            </header>

            {error && <div className={styles.error}>{error}</div>}

            <section className={styles.filters}>
                <div className={styles.filterHeader}>
                    <div>
                        <h3>Filters</h3>
                        <p>Select a numeric measurement step, then narrow by software version, testspec, or date.</p>
                    </div>
                    <button onClick={handleResetFilters} className={styles.resetButton}>Reset</button>
                </div>

                <div className={styles.filterGrid}>
                    <div className={styles.filterField}>
                        <label>Step number</label>
                        <select value={selectedStepNumber} onChange={(event) => setSelectedStepNumber(event.target.value)}>
                            <option value="">Select step number</option>
                            {stepNumbers.map((step) => (
                                <option key={step.stepNumber} value={step.stepNumber}>
                                    {step.stepNumber} - {step.stepName || 'Unnamed step'} ({step.count})
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className={styles.filterField}>
                        <label>ATE SW version</label>
                        <select value={selectedAteSwVersion} onChange={(event) => setSelectedAteSwVersion(event.target.value)}>
                            <option value="">All versions</option>
                            {ateSwVersions.map((version) => (
                                <option key={version} value={version}>{version}</option>
                            ))}
                        </select>
                    </div>

                    <div className={styles.filterField}>
                        <label>Testspec</label>
                        <select value={selectedTestspec} onChange={(event) => setSelectedTestspec(event.target.value)}>
                            <option value="">All testspecs</option>
                            {testspecs.map((spec) => (
                                <option key={spec} value={spec}>{spec}</option>
                            ))}
                        </select>
                    </div>

                    <div className={styles.datePickers}>
                        <label>Start date</label>
                        <DatePicker
                            selected={startDate}
                            onChange={(date) => setStartDate(date)}
                            dateFormat="yyyy-MM-dd"
                            placeholderText="Any start date"
                            isClearable
                        />
                    </div>

                    <div className={styles.datePickers}>
                        <label>End date</label>
                        <DatePicker
                            selected={endDate}
                            onChange={(date) => setEndDate(date)}
                            dateFormat="yyyy-MM-dd"
                            placeholderText="Any end date"
                            isClearable
                            minDate={startDate}
                        />
                    </div>
                </div>

                <div className={styles.buttons}>
                    <span className={styles.filterHint}>
                        {selectedStepNumber
                            ? `Code considered: ${selectedTestspec || 'all testspecs'}; step ${selectedStepNumber}`
                            : 'Choose a step to generate the chart'}
                    </span>
                    <button onClick={fetchNormalDistributionData} className={styles.applyButton}>
                        {loading ? 'Loading...' : 'Apply filters'}
                    </button>
                </div>

                <SourceFileSummary
                    compact
                    startDate={startDate}
                    endDate={endDate}
                    ateSwVersion={selectedAteSwVersion}
                    testspec={selectedTestspec}
                    selectedFamilies={selectedFamilies}
                    onFamiliesChange={setSelectedFamilies}
                    title="Source files used for dashboard charts"
                />
            </section>

            {normalDistData && (
                <section className={styles.metrics}>
                    {metrics.map((metric) => (
                        <div className={`${styles.metricCard} ${styles[metric.tone]}`} key={metric.label}>
                            <span>{metric.label}</span>
                            <strong>{metric.value}</strong>
                        </div>
                    ))}
                </section>
            )}

            <section className={styles.chartPanel}>
                <div className={styles.panelHeader}>
                    <div>
                        <h3>Distribution fit</h3>
                        <p>
                            {normalDistData
                                ? `Step ${normalDistData.stepNumber}${normalDistData.stepName ? ` - ${normalDistData.stepName}` : ''}; ${normalDistData.recordCount || 0} records from ${Number(normalDistData.minVal).toFixed(3)} to ${Number(normalDistData.maxVal).toFixed(3)}.`
                                : 'Histogram counts are built from minimum to maximum value with small ordered intervals.'}
                        </p>
                    </div>
                    {normalDistData && (
                        <div className={styles.chartActions}>
                            <button onClick={() => setIsModalOpen(true)} className={styles.enlargeButton}>Make Bigger</button>
                            <button onClick={() => exportChartAsPNG(chartRef, 'normal_distribution_chart')} className={styles.enlargeButton}>Export as PNG</button>
                        </div>
                    )}
                </div>

                <div className={styles.chartContainer} ref={chartRef}>
                    {loading ? (
                        <div className={styles.loadingState}>Loading distribution data...</div>
                    ) : normalDistData ? (
                        <Chart type="bar" data={chartData} options={chartOptions} />
                    ) : (
                        <div className={styles.emptyState}>Select a step and apply filters to generate the distribution.</div>
                    )}
                </div>
            </section>

            <Modal
                isOpen={isModalOpen}
                onRequestClose={() => setIsModalOpen(false)}
                contentLabel="Expanded Chart"
                className={styles.modal}
                overlayClassName={styles.overlay}
            >
                <button onClick={() => setIsModalOpen(false)} className={styles.closeButton}>Close</button>
                {normalDistData && (
                    <div className={styles.expandedChartContainer}>
                        <Chart type="bar" data={chartData} options={chartOptions} />
                    </div>
                )}
            </Modal>
        </div>
    );
};

export default NormalDistributionChartWithDropdown;
